"use client";

import React, { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

const sizeStyles = {
  sm: "max-w-[300px]",
  md: "max-w-[500px]",
  lg: "max-w-[800px]",
  xl: "max-w-[1140px]",
};

/**
 * Modal - Dialog overlay.
 *
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onHide - Called when modal should close
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {boolean} centered - Vertically center the modal
 * @param {boolean|string} backdrop - true | false | 'static' (static prevents close on backdrop click)
 * @param {boolean} keyboard - Whether ESC closes the modal
 * @param {function} onEntered - Called after modal is shown
 * @param {string} dialogClassName - Class on the dialog element
 */
function ModalComponent({
  children,
  show = false,
  onHide,
  size = "md",
  centered = false,
  backdrop = true,
  keyboard = true,
  onEntered,
  dialogClassName = "",
  className = "",
  style,
  ...props
}) {
  const enteredRef = useRef(false);

  useEffect(() => {
    if (!show || !keyboard) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && onHide) onHide();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, keyboard, onHide]);

  useEffect(() => {
    if (!show) {
      enteredRef.current = false;
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  useEffect(() => {
    if (show && onEntered && !enteredRef.current) {
      enteredRef.current = true;
      onEntered();
    }
  }, [show, onEntered]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (
        e.target === e.currentTarget &&
        backdrop !== "static" &&
        backdrop !== false &&
        onHide
      ) {
        onHide();
      }
    },
    [backdrop, onHide]
  );

  if (!show || typeof document === "undefined") return null;

  const sizeClass = sizeStyles[size] || sizeStyles.md;

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/50 z-[1050]" />
      <div
        className={`fixed inset-0 z-[1051] overflow-y-auto ${className}`.trim()}
        style={style}
        onClick={handleBackdropClick}
        {...props}
      >
        <div
          className={`flex min-h-full ${
            centered ? "items-center" : "items-start pt-12"
          } justify-center p-4`}
          onClick={handleBackdropClick}
        >
          <div
            className={`relative w-full ${sizeClass} bg-white rounded-lg shadow-xl ${dialogClassName}`.trim()}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * Modal.Header / ModalHeader
 *
 * @param {boolean} closeButton - Show close X button
 */
function ModalHeader({
  children,
  closeButton = false,
  onHide,
  onClick,
  className = "",
  style,
  ...props
}) {
  const handleClose = (e) => {
    if (onClick) onClick(e);
    if (onHide) onHide();
  };

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 border-b border-lightGrey3 ${className}`.trim()}
      style={style}
      {...props}
    >
      <div className="flex-1">{children}</div>
      {closeButton && (
        <button
          type="button"
          onClick={handleClose}
          className="ml-2 text-2xl leading-none text-darkGrey hover:text-darkBlack cursor-pointer"
          aria-label="Close"
        >
          &times;
        </button>
      )}
    </div>
  );
}

/**
 * Modal.Body / ModalBody
 */
function ModalBody({ children, className = "", style, ...props }) {
  return (
    <div className={`px-4 py-3 ${className}`.trim()} style={style} {...props}>
      {children}
    </div>
  );
}

/**
 * Modal.Footer / ModalFooter
 */
function ModalFooter({ children, className = "", ...props }) {
  return (
    <div
      className={`flex items-center justify-end gap-2 px-4 py-3 border-t border-lightGrey3 ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Modal.Title / ModalTitle
 */
function ModalTitle({ children, className = "", ...props }) {
  return (
    <h5 className={`text-lg font-medium ${className}`.trim()} {...props}>
      {children}
    </h5>
  );
}

function ModalWithContext(modalProps) {
  const { children, onHide, ...rest } = modalProps;

  const childrenWithOnHide = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === ModalHeader) {
      return React.cloneElement(child, { onHide });
    }
    return child;
  });

  return (
    <ModalComponent onHide={onHide} {...rest}>
      {childrenWithOnHide}
    </ModalComponent>
  );
}

export const Modal = Object.assign(ModalWithContext, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  Title: ModalTitle,
});

export { ModalHeader, ModalBody, ModalFooter, ModalTitle };
